import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import type { ExhibitorAnalyticsDto } from "@mvk/shared";
import { LeadsListView } from "../../../components/LeadsListView";
import { useAdminAuth } from "../../auth/AdminAuthContext";
import { adminAnalyticsApi } from "../../../services/adminAnalyticsApi";

/** Outbound scans by booth staff + managers / profiling block. */
export function ExhibitorYouScannedPage() {
  const { user } = useAdminAuth();
  const eventId = user?.assignedEventId ?? "";
  const companyId = user?.assignedCompanyId ?? "";
  const [analytics, setAnalytics] = useState<ExhibitorAnalyticsDto | null>(null);
  const [loadingManagers, setLoadingManagers] = useState(true);

  useEffect(() => {
    if (!eventId || !companyId) {
      setLoadingManagers(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingManagers(true);
      try {
        const res = await adminAnalyticsApi.getExhibitor(eventId, companyId);
        if (!cancelled) setAnalytics(res.analytics);
      } catch {
        if (!cancelled) setAnalytics(null);
      } finally {
        if (!cancelled) setLoadingManagers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, companyId]);

  if (!eventId || !companyId) {
    return (
      <div className="p-6 text-gray-500 text-[14px]">
        Нет доступа. Войдите как экспонент.
      </div>
    );
  }

  const reps = analytics?.representatives ?? [];
  const managersLeadMarks = analytics?.managersLeadMarks;
  const leadMarks = analytics?.leadMarks;

  return (
    <div className="space-y-5">
      <LeadsListView
        eventId={eventId}
        companyId={companyId}
        basePath="/exhibitor/you-scanned"
        audience="admin"
        showBack={false}
        direction="outbound"
        title="Вы сканировали"
        subtitle="Исходящие сканы билетов посетителей · пометки и заметки участника"
      />

      <div className="px-4 sm:px-6 max-w-5xl mx-auto pb-8">
        {loadingManagers ? (
          <div className="flex justify-center text-gray-400 py-6">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : reps.length > 0 ? (
          <section className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-4 pt-4 pb-2">
              <h2 className="text-[13px] text-gray-900">Менеджеры / профилирование</h2>
              <p className="text-[11px] text-gray-400">
                Лиды после скана бейджа менеджера: с пометками, без пометок, «Спам»
              </p>
            </div>

            {managersLeadMarks || leadMarks ? (
              <div className="px-4 pb-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  {
                    label: "Профилированные",
                    value: managersLeadMarks?.profiled ?? 0,
                    className: "text-emerald-700 bg-emerald-50",
                  },
                  {
                    label: "Не профилированные",
                    value: managersLeadMarks?.unprofiled ?? 0,
                    className: "text-amber-700 bg-amber-50",
                  },
                  {
                    label: "Спам",
                    value: managersLeadMarks?.spam ?? 0,
                    className: "text-slate-700 bg-slate-100",
                  },
                  {
                    label: "Итого (менеджеры)",
                    value: managersLeadMarks?.total ?? 0,
                    className: "text-indigo-700 bg-indigo-50",
                  },
                ].map((card) => (
                  <div key={card.label} className={`rounded-xl px-3 py-2.5 ${card.className}`}>
                    <div className="text-[20px] font-semibold leading-none">{card.value}</div>
                    <div className="text-[11px] mt-1 font-medium leading-snug">{card.label}</div>
                  </div>
                ))}
              </div>
            ) : null}

            {leadMarks && leadMarks.total !== (managersLeadMarks?.total ?? 0) ? (
              <p className="px-4 pb-2 text-[11px] text-gray-400">
                Все лиды компании: {leadMarks.total} (проф. {leadMarks.profiled} · без пометок{" "}
                {leadMarks.unprofiled} · спам {leadMarks.spam})
              </p>
            ) : null}

            <div className="overflow-x-auto">
              <table className="w-full text-left text-[12px]">
                <thead>
                  <tr className="border-t border-gray-50 text-gray-400">
                    <th className="px-4 py-2 font-normal">Менеджер</th>
                    <th className="px-2 py-2 font-normal text-right">Сканы</th>
                    <th className="px-2 py-2 font-normal text-right">Проф.</th>
                    <th className="px-2 py-2 font-normal text-right">Без пометок</th>
                    <th className="px-2 py-2 font-normal text-right">Спам</th>
                    <th className="px-4 py-2 font-normal text-right">Итого</th>
                  </tr>
                </thead>
                <tbody>
                  {reps.map((r) => (
                    <tr key={r.representativeId} className="border-t border-gray-50">
                      <td className="px-4 py-2.5">
                        <div className="text-gray-900">{r.fullName}</div>
                        {r.position ? (
                          <div className="text-[10px] text-gray-400">{r.position}</div>
                        ) : null}
                      </td>
                      <td className="px-2 py-2.5 text-indigo-600 text-right">{r.badgeScans}</td>
                      <td className="px-2 py-2.5 text-emerald-700 text-right">{r.leadsProfiled}</td>
                      <td className="px-2 py-2.5 text-amber-700 text-right">{r.leadsUnprofiled}</td>
                      <td className="px-2 py-2.5 text-slate-600 text-right">{r.leadsSpam}</td>
                      <td className="px-4 py-2.5 text-gray-900 font-medium text-right">
                        {r.leadsTotal}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : (
          <p className="text-[12px] text-gray-400 px-1">
            Добавьте представителей — здесь появится профилирование менеджеров.
          </p>
        )}
      </div>
    </div>
  );
}
